import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Post from "../components/Post";
import FullScreenViewer from "../components/FullScreenVersion/FullScreenViewer";
import ResetPassword from "../components/ResetPassword";
import { useGlobalState } from "../contexts/GlobalStateContext";
import { createPortal } from "react-dom";

const FetchPosts = ({
  disableAutoFill = false,
  pageParam,
  username,
  searchQuery,
  categoryID,
  subcategoryID,
  page,
  context,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const v = params.get("v");
  const email = params.get("e");
  const code = params.get("c");
  const post = params.get("id");
  const { addLike, globalState, setJustPosted, resetRefreshKey, setFullScreenPost, setIsCreateVisible, setExitedPost } = useGlobalState();
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(v === "rp");
  const { urlPageName } = useParams();
  let pageName = location.pathname.split("/")[1] || "recent";
  const initialPostSerial = globalState.fullScreenPost?.serial;
  const emptyFetchCount = useRef(0);
  const fetchLockRef = useRef(false);
  const postRefs = useRef({});


  if (!page) {
    if (!pageName) {
      pageName = "recent";
    }
    if (pageName === "popular") {
      pageName = pageParam || urlPageName || "recent";
    }
    if (searchQuery || categoryID || subcategoryID) {
      pageName = "search";
    }
  } else if (page === "resetPassword" || page === "post") {
    pageName = "recent";
  } else {
    pageName = page;
  }



  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noMorePosts, setNoMorePosts] = useState(false);
  const [lastSeen, setLastSeen] = useState(null);
  const directionRef = useRef("down");
  const allPosts = useRef([]);
  const fetchingRef = useRef(false);
  const scrollTimeout = useRef(null);

  // Inside FetchPosts component
  const fullScreenPost = globalState.fullScreenPost;
  const isExternalClick = fullScreenPost?.isExternalClick === true;

  // Define the posts list so the clicked post is ALWAYS the first item
  const viewerPosts = (() => {
    if (!fullScreenPost) return [];

    if (isExternalClick) {
      return [fullScreenPost];
    }

    // Find the clicked post in your current list
    const initialIdx = posts.findIndex((p) => p.uuid === fullScreenPost.uuid);

    if (initialIdx !== -1) {
      // Slice the array to start FROM the clicked post
      // This makes the clicked post index 0, so it renders at the top immediately.
      return posts.slice(initialIdx);
    }

    return [fullScreenPost];
  })();


  const fetchPosts = useCallback(
    (useLastSeen = false, overrideLastSeenSerial = null) => {
      if (loading || fetchingRef.current) {
        console.log("⏳ Fetch skipped (loading or already fetching)");
        return;
      }

      fetchingRef.current = true;
      setLoading(true);

      let url = `${baseUrl}/php/get_posts.php?pageName=${encodeURIComponent(
        pageName
      )}`;

      let serialToUse = null;
      if (overrideLastSeenSerial) {
        serialToUse = overrideLastSeenSerial;
      } else if (useLastSeen && lastSeen) {
        serialToUse = lastSeen;
      }

      if (serialToUse) {
        url += `&lastSeen=${serialToUse}&direction=${directionRef.current}`;
      }
      if (categoryID) url += `&categoryId=${categoryID}`;
      if (subcategoryID) url += `&subcategoryId=${subcategoryID}`;
      if (searchQuery) url += `&query=${searchQuery}`;
      if (username) url += `&username=${username}`;
      if (context) url += `&context=${context}`;

      fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.text())
        .then((text) => {
          let data;
          try {
            data = JSON.parse(text);
          } catch {
            throw new Error("Invalid JSON");
          }

          if (!data || data.length === 0) {
            emptyFetchCount.current += 1;
            if (emptyFetchCount.current >= 3) setNoMorePosts(true);

            setLoading(false);
            fetchingRef.current = false;
            fetchLockRef.current = false;
            return;
          }

          emptyFetchCount.current = 0;

          const postIds = data.map((p) => p.uuid).join(",");

          // Inside fetchPosts.jsx within the fetchPosts callback

          const finish = (postsWithLikes) => {
            // 1. Identify truly new posts
            const newPosts = postsWithLikes.filter(
              (p) => !allPosts.current.some((e) => e.uuid === p.uuid)
            );

            if (newPosts.length === 0) {
              const last = postsWithLikes[postsWithLikes.length - 1];
              if (last?.serial) setLastSeen(last.serial);
              setLoading(false);
              fetchingRef.current = false;
              fetchLockRef.current = false;
              return;
            }

            // 2. Update the tracking refs and state
            allPosts.current = [...allPosts.current, ...newPosts];

            // 3. Add the likes to Global State if they exist
            const newlyLikedIds = newPosts.filter(p => p.liked).map(p => p.uuid);
            if (newlyLikedIds.length > 0) {
              addLike(newlyLikedIds);
            }

            setPosts((prev) => [...prev, ...newPosts]);
            setLastSeen(newPosts[newPosts.length - 1].serial);

            setLoading(false);
            fetchingRef.current = false;
            fetchLockRef.current = false;
          };

          if (globalState.isAuthenticated) {
            fetch(`${baseUrl}/php/fetch_likes_states.php?postIds=${postIds}`, {
              credentials: "include",
            })
              .then((r) => r.json())
              .then((likes) =>
                finish(
                  data.map((p) => ({
                    ...p,
                    liked: likes[p.uuid] || false,
                  }))
                )
              )
              .catch(() =>
                finish(data.map((p) => ({ ...p, liked: false })))
              );
          } else {
            finish(data.map((p) => ({ ...p, liked: false })));
          }
        })
        .catch(() => {
          setLoading(false);
          fetchingRef.current = false;
          fetchLockRef.current = false;
        });
    },
    [pageName, lastSeen, addLike, globalState.isAuthenticated]
  );


  const requestFetch = useCallback(
    (useLastSeen = true) => {
      if (fetchLockRef.current) return;

      fetchLockRef.current = true;
      directionRef.current = "down";
      fetchPosts(useLastSeen);
    },
    [fetchPosts]
  );


  useEffect(() => {
    if (!globalState.isAuthenticated || allPosts.current.length === 0) return;

    const postIds = allPosts.current.map((post) => post.uuid).join(",");

    fetch(`${baseUrl}/php/fetch_likes_states.php?postIds=${postIds}`, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((likeStates) => {
        const likedPostIds = Object.entries(likeStates)
          .filter(([uuid, liked]) => liked)
          .map(([uuid]) => uuid);

        if (likedPostIds.length > 0) addLike(likedPostIds);

        setPosts((prevPosts) =>
          prevPosts.map((post) => ({
            ...post,
            liked: likeStates[post.uuid] || false,
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching like states after login:", error);
      });
  }, [globalState.isAuthenticated]);

  useEffect(() => {
    setPosts([]);
    setLastSeen(null);
    setNoMorePosts(false);
    allPosts.current = [];
    emptyFetchCount.current = 0;
    requestFetch(false);
  }, [pageName, username]);



  const postContainerRef = useRef(null);


  const isFullscreen = Boolean(globalState.fullScreenPost);

  useEffect(() => {
    if (
      disableAutoFill ||
      loading ||
      noMorePosts ||
      isFullscreen // 🔒 critical
    ) {
      return;
    }

    const pageHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;

    if (pageHeight < viewportHeight * 1.5 || posts.length < 4) {
      requestFetch(true);
    }
  }, [
    posts.length,
    loading,
    disableAutoFill,
    noMorePosts,
    isFullscreen,
    requestFetch
  ]);

const handleExitFullscreen = (closedPost) => {
  console.log("handleExitFullscreen:", closedPost.uuid);
  setFullScreenPost(null);
  setExitedPost(closedPost);

  // Retry scrolling until the element exists (max 10 attempts)
  let attempts = 0;
  const scrollToPost = () => {
    const el = postRefs.current[closedPost.uuid];
    if (!el || !postContainerRef.current) {
      attempts += 1;
      if (attempts < 10) {
        setTimeout(scrollToPost, 50); // retry in 50ms
      }
      return;
    }

    // Scroll smoothly
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  setTimeout(scrollToPost, 50);
};


  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.body.offsetHeight;

    if (
      scrollY + windowHeight >= documentHeight - 400 &&
      !loading &&
      !noMorePosts
    ) {
      directionRef.current = "down";
      requestFetch(true);
    } else if (
      scrollY <= 200 &&
      !loading &&
      !noMorePosts &&
      allPosts.current.length > posts.length
    ) {
      directionRef.current = "up";
      const newPosts = allPosts.current.slice(0, posts.length + 10);
      setPosts(newPosts);
    }
  }, [requestFetch, loading, noMorePosts, posts.length]);

  const throttledScroll = useCallback(() => {
    if (scrollTimeout.current) return;

    scrollTimeout.current = setTimeout(() => {
      handleScroll();
      scrollTimeout.current = null;
    }, 200);
  }, [handleScroll]);

  useEffect(() => {
    if (globalState.fullScreenPost) return; // 🔒 skip for fullscreen mode
    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [throttledScroll, globalState.fullScreenPost]);


  const [embeddedPost, setEmbeddedPost] = useState(null);

const handleCloseEmbedded = () => {
  setEmbeddedPost(null);

  const newParams = new URLSearchParams(location.search);
  newParams.delete("id");

  const newSearch = newParams.toString();

  navigate(
    {
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : "",
    },
    { replace: true }
  );
};

  useEffect(() => {
    // Fetch the embedded post if it's not a text message
    if (post) {
      const fetchPost = async () => {
        try {
          const response = await fetch(`${baseUrl}/php/fetch_post.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ uuid: post }),
          });

          const data = await response.json();

          if (data.success) {

            // ✅ SOLUTION: Create a handler that does both actions
            const handleImageClick = () => {
              setFullScreenPost(data.post); // 1. Open the fullscreen viewer
              handleCloseEmbedded();       // 2. Close this embedded modal
            };

            setEmbeddedPost(
              <Post
                key={data.post.uuid}
                post={data.post}
                onImageClick={handleImageClick} // 3. Pass the new handler
              />
            );
          } else {
            console.error("Failed to fetch post:", data.message);
          }
        } catch (error) {
          console.error("Error fetching post:", error);
        }
      };

      fetchPost();
    }
  }, [post, baseUrl, setFullScreenPost]); // <-- Added dependencies

  // 👇 Infinite scroll intersection observer fix
  const bottomRef = useRef(null);

  useEffect(() => {
    if (
      username ||               // ⛔ profile pages
      disableAutoFill ||
      !bottomRef.current ||
      posts.length === 0
    ) return;

    // Only observe if content height exceeds viewport
    if (document.body.offsetHeight < window.innerHeight * 1.5) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loading && !noMorePosts) {
          console.log("🟢 Loading more posts (intersection triggered)");
          directionRef.current = "down";
          requestFetch(true);
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [loading, noMorePosts, requestFetch, posts.length]);


  return (
    <>
      {embeddedPost &&
        createPortal(
          <div
            id="showLink"
            className="post_modal"
            style={{ display: "block" }}
          >
            <div
              className="post_container"
              style={{ padding: "30px 2px 2px" }}
            >
              <span
                className="close"
                onClick={handleCloseEmbedded}
                role="button"
                tabIndex={0}
                aria-label="Close modal"
              >
                &times;
              </span>
              {embeddedPost}
            </div>
          </div>,
          document.body
        )}

      


    <div
          id={pageName}
          ref={postContainerRef}
          className={username ? "profile_posts" : "tabcontent"}
        >
      {posts.map(post => (
        <div
        className={`container post-${post.uuid}`}
          key={post.uuid}
          ref={el => {
            if (el) postRefs.current[post.uuid] = el;
          }}
        >
          <Post
            post={post}
            pageName={pageName}
            onImageClick={() => setFullScreenPost(post)}
          />
        </div>
      ))}
      </div>

      {/* Trigger more fetch when scrolled to bottom */}
      {!loading && !noMorePosts && <div ref={bottomRef} style={{ height: 1 }} />}


      {showResetPasswordModal && (
        <ResetPassword
          email={email}
          code={code}
          onClose={() => setShowResetPasswordModal(false)}
        />
      )}
    </>
  );
};

export default FetchPosts;
